import React, { useState, useEffect } from 'react';
import { SectionTitle, Card, Button } from './UIComponents'; // UIComponents dosyanızın doğru yolda olduğundan emin olun
import { sizeTemplates } from '../models/sizeTemplates'; // sizeTemplates dosyanızın doğru yolda olduğundan emin olun
import { toast } from 'react-toastify';

// a.tsx'den alınan SizeSpec arayüzü
interface SizeSpec {
  size: string;
  [key: string]: string | number;
}

interface StitchDetail {
  location: string;
  stitchType: string;
  threadType: string;
  stitchesPerInch: number;
  notes: string;
  imageUrls: string[];
}

interface FabricSpec {
  composition: string;
  weight: string;
  construction: string;
  supplier: string;
  colorFastness: string;
  shrinkage: string;
}

interface PrintingDetail {
  technique: string;
  location: string;
  colors: string;
  size: string;
  notes: string;
  imageUrls: string[];
}

export interface TechnicalSpec {
  _id?: string;
  sizeSpecs: SizeSpec[];
  measurementFields: string[]; // Bu, dinamik tablo satırları tarafından yönetilecek
  sizeSpecFields: string[]; // Bu, dinamik tablo sütunları tarafından yönetilecek (bedenler)
  stitchDetails: StitchDetail[];
  fabricSpecs: FabricSpec[];
  printingDetails: PrintingDetail[];
  careInstructions: string[];
  qualityStandards: string[];
  notes: string;
  technicalDrawingUrls?: string[];
  measurementGuideUrls?: string[];
  modelDocumentUrls?: string[];
  dynamicSizeTable?: { // Bu, a.tsx'deki tablo yapısıyla uyumlu
    rows: string[];
    columns: string[];
    data: { [key: string]: { [key: string]: string } };
  };
}

interface TechnicalSpecSheetProps {
  techSpec: TechnicalSpec;
  setTechSpec: (spec: TechnicalSpec) => void;
}

const standardSizes = {
  tops: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
  bottoms: ['28', '30', '32', '34', '36', '38'],
  numeric: ['0', '2', '4', '6', '8', '10'],
  kids: ['2T', '3T', '4T', '5T', '6T'],
  shoes: ['6', '7', '8', '9', '10', '11']
};

type SizeCategory = 'tops' | 'bottoms' | 'numeric' | 'kids' | 'shoes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8012';

const TechnicalSpecSheet: React.FC<TechnicalSpecSheetProps> = ({ techSpec, setTechSpec }) => {
  // Mevcut state'ler (ölçü tablosu dışındakiler)
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  // a.tsx'den alınan ölçü tablosu ve modal için state'ler
  const [selectedSizeCategory, setSelectedSizeCategory] = useState<SizeCategory>('tops');
  const [selectedTemplateCategory, setSelectedTemplateCategory] = useState<string>("Üst Giyim"); // veya başlangıçta boş bırakılabilir: useState<string>("");
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");

  const [dynamicTableRows, setDynamicTableRows] = useState<string[]>(techSpec.dynamicSizeTable?.rows || []);
  const [dynamicTableColumns, setDynamicTableColumns] = useState<string[]>(techSpec.dynamicSizeTable?.columns || []);
  const [dynamicTableData, setDynamicTableData] = useState<{ [key: string]: { [key: string]: string } }>(techSpec.dynamicSizeTable?.data || {});

  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [modalInput, setModalInput] = useState<string>('');
  const [modalType, setModalType] = useState<'column' | 'row'>('column');

  const [editingRowName, setEditingRowName] = useState<string | null>(null);
  const [editingRowInput, setEditingRowInput] = useState<string>('');
  const [editingColName, setEditingColName] = useState<string | null>(null);
  const [editingColInput, setEditingColInput] = useState<string>('');

  // --- a.tsx'den kopyalanan ölçü tablosu fonksiyonları ---

  // Modal işlemleri için fonksiyonlar (a.tsx'den)
  const handleModalSubmit = () => {
    console.log('Modal submit:', { modalType, modalInput });
    if (!modalInput.trim()) return;

    if (modalType === 'row') {
      addTableRow(modalInput.trim());
    } else {
      addTableColumn(modalInput.trim());
    }

    setModalInput('');
    setModalVisible(false);
  };

  // Beden presetleri için fonksiyonlar (a.tsx'den addSizeSpec)
  const addSizeSpecFromPreset = () => { // Fonksiyon adını değiştirdim çakışmayı önlemek için, bu a.tsx'deki addSizeSpec
    const selectedSizeValue = (document.getElementById('sizeSelect') as HTMLSelectElement).value;
    console.log('Seçilen beden:', selectedSizeValue);
    
    if (!selectedSizeValue || dynamicTableColumns.includes(selectedSizeValue)) {
      toast.warn(`Beden "${selectedSizeValue}" zaten mevcut veya seçilmedi.`);
      console.log('Beden eklenemedi:', { selectedSizeValue, mevcutBedenler: dynamicTableColumns });
      return;
    }

    const newColumns = [...dynamicTableColumns, selectedSizeValue];
    const newData = { ...dynamicTableData };
    
    dynamicTableRows.forEach(row => {
      if (!newData[row]) newData[row] = {};
      newData[row][selectedSizeValue] = ''; // Yeni sütun için boş değer
    });
  
    console.log('Yeni beden (sütun) eklendi:', selectedSizeValue);
    updateTableData(dynamicTableRows, newColumns, newData);
  };


  // Dinamik tablo için yardımcı fonksiyonlar (a.tsx'den)
  const updateTableData = (rows: string[], columns: string[], data: { [key: string]: { [key: string]: string } }) => {
    console.log('Tablo verileri güncelleniyor:', { rows, columns, data });
    
    setDynamicTableRows(rows);
    setDynamicTableColumns(columns);
    setDynamicTableData(data);

    const updatedSizeSpecs = columns.map(size => ({
      size,
      ...Object.fromEntries(rows.map(field => [field, data[field]?.[size] || '']))
    }));

    console.log('Güncellenmiş sizeSpecs:', updatedSizeSpecs);

    setTechSpec((prevSpec: TechnicalSpec) => {
      const updatedSpecData = {
        ...prevSpec,
        sizeSpecs: updatedSizeSpecs,
        measurementFields: rows, // techSpec.measurementFields'ı güncelle
        dynamicSizeTable: { // techSpec.dynamicSizeTable'ı da güncelle
          rows,
          columns,
          data
        }
      };
      console.log('Güncellenmiş techSpec:', updatedSpecData);
      return updatedSpecData;
    });
  };

  const addTableRow = (rowName: string) => {
    console.log('Yeni satır ekleniyor:', rowName);
    if (!rowName.trim() || dynamicTableRows.includes(rowName.trim())) {
        toast.warn(`Satır "${rowName.trim()}" zaten mevcut veya geçersiz.`);
        return;
    }

    const newRows = [...dynamicTableRows, rowName.trim()];
    const newData = { ...dynamicTableData };
    
    newData[rowName.trim()] = {};
    dynamicTableColumns.forEach(col => {
      newData[rowName.trim()][col] = '';
    });

    console.log('Yeni satır verileri:', { newRows, newData });
    updateTableData(newRows, dynamicTableColumns, newData);
  };

  const addTableColumn = (columnName: string) => {
    console.log('Yeni sütun ekleniyor:', columnName);
    if (!columnName.trim() || dynamicTableColumns.includes(columnName.trim())) {
        toast.warn(`Sütun "${columnName.trim()}" zaten mevcut veya geçersiz.`);
        return;
    }

    const newColumns = [...dynamicTableColumns, columnName.trim()];
    const newData = { ...dynamicTableData };
    
    dynamicTableRows.forEach(row => {
      if (!newData[row]) newData[row] = {};
      newData[row][columnName.trim()] = '';
    });

    console.log('Yeni sütun verileri:', { newColumns, newData });
    updateTableData(dynamicTableRows, newColumns, newData);
  };

  const updateTableCell = (row: string, column: string, value: string) => {
    const newData = JSON.parse(JSON.stringify(dynamicTableData)); // Deep copy
    if (!newData[row]) newData[row] = {};
    newData[row][column] = value;
    updateTableData(dynamicTableRows, dynamicTableColumns, newData);
  };

  const removeTableRow = (rowIndex: number) => {
    const rowToRemove = dynamicTableRows[rowIndex];
    const newRows = dynamicTableRows.filter((_, index) => index !== rowIndex);
    const newData = { ...dynamicTableData };
    delete newData[rowToRemove];
    updateTableData(newRows, dynamicTableColumns, newData);
  };

  const removeTableColumn = (columnIndex: number) => {
    const columnToRemove = dynamicTableColumns[columnIndex];
    const newColumns = dynamicTableColumns.filter((_, index) => index !== columnIndex);
    const newData = { ...dynamicTableData };
    Object.keys(newData).forEach(row => {
      delete newData[row][columnToRemove];
    });
    updateTableData(dynamicTableRows, newColumns, newData);
  };

  const handleRowNameEdit = (rowName: string) => {
    setEditingRowName(rowName);
    setEditingRowInput(rowName);
  };

  const handleRowNameSave = () => {
    if (editingRowName && editingRowInput && editingRowInput.trim() !== "") {
      if (editingRowInput.trim() !== editingRowName && dynamicTableRows.includes(editingRowInput.trim())) {
        toast.error(`Satır adı "${editingRowInput.trim()}" zaten mevcut.`);
        return;
      }
      const newRows = dynamicTableRows.map(row => 
        row === editingRowName ? editingRowInput.trim() : row
      );
      
      const newData = { ...dynamicTableData };
      if (editingRowInput.trim() !== editingRowName) {
        newData[editingRowInput.trim()] = { ...newData[editingRowName] };
        delete newData[editingRowName];
      }
      updateTableData(newRows, dynamicTableColumns, newData);
    }
    setEditingRowName(null);
    setEditingRowInput('');
  };

  const handleRowNameCancel = () => {
    setEditingRowName(null);
    setEditingRowInput('');
  };

  const handleColNameEdit = (colName: string) => {
    setEditingColName(colName);
    setEditingColInput(colName);
  };

  const handleColNameSave = () => {
    if (editingColName && editingColInput && editingColInput.trim() !== "") {
      if (editingColInput.trim() !== editingColName && dynamicTableColumns.includes(editingColInput.trim())) {
        toast.error(`Sütun adı "${editingColInput.trim()}" zaten mevcut.`);
        return;
      }
      const newColumns = dynamicTableColumns.map(col =>
        col === editingColName ? editingColInput.trim() : col
      );
      const newData = { ...dynamicTableData };
      if (editingColInput.trim() !== editingColName) {
        Object.keys(newData).forEach(row => {
          if (newData[row]) {
            newData[row][editingColInput.trim()] = newData[row][editingColName];
            delete newData[row][editingColName];
          }
        });
      }
      updateTableData(dynamicTableRows, newColumns, newData);
    }
    setEditingColName(null);
    setEditingColInput('');
  };

  const handleColNameCancel = () => {
    setEditingColName(null);
    setEditingColInput('');
  };

  // a.tsx'den addSizeTemplate
  const addSizeTemplate = (templateName: string) => {
    console.log('Preset seçildi:', templateName);
    
    const categoryKey = Object.keys(sizeTemplates).find(cat => 
        (sizeTemplates as any)[cat].some((template: { name: string; }) => template.name === templateName)
    );

    if (!categoryKey) {
        console.log('Kategori bulunamadı');
        toast.error('Şablon kategorisi bulunamadı.');
        return;
    }

    const template = (sizeTemplates as any)[categoryKey].find((t: { name: string; }) => t.name === templateName);
    
    if (!template || !template.measurements || !template.sizes) {
        console.log('Template, ölçüler veya bedenler bulunamadı:', template);
        toast.error('Şablon yapısı eksik veya hatalı.');
        return;
    }

    console.log('Template bulundu:', template);

    const measurementFieldsFromTemplate = Object.keys(template.measurements);
    const sizesFromTemplate = template.sizes;

    const tableDataFromTemplate: { [key: string]: { [key: string]: string } } = {};
    measurementFieldsFromTemplate.forEach((field: string) => {
        tableDataFromTemplate[field] = {};
        sizesFromTemplate.forEach((size: string) => {
            tableDataFromTemplate[field][size] = template.measurements[field][size] || '';
        });
    });

    console.log('Yeni tablo verileri:', {
        rows: measurementFieldsFromTemplate,
        columns: sizesFromTemplate,
        data: tableDataFromTemplate
    });
    
    updateTableData(measurementFieldsFromTemplate, sizesFromTemplate, tableDataFromTemplate);
    toast.success(`"${templateName}" preseti başarıyla uygulandı.`);
  };

  // a.tsx'den renderTemplateSelector
  const renderTemplateSelector = () => (
    <div className="space-y-4 mb-6">
      <div className="flex flex-col sm:flex-row items-center gap-2">
        <select
          className="px-3 py-2 border border-gray-300 rounded-md text-sm w-full sm:w-auto flex-1"
          value={selectedTemplateCategory}
          onChange={(e) => {
            setSelectedTemplateCategory(e.target.value);
            setSelectedTemplate(""); // Kategori değiştiğinde preset seçimini sıfırla
          }}
        >
          <option value="">Kategori Seçin</option>
          {Object.keys(sizeTemplates).map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
        <select
          className="px-3 py-2 border border-gray-300 rounded-md text-sm w-full sm:w-auto flex-2"
          value={selectedTemplate || ""}
          onChange={(e) => setSelectedTemplate(e.target.value)}
          disabled={!selectedTemplateCategory}
        >
          <option value="">Preset Seçin</option>
          {selectedTemplateCategory && (sizeTemplates as any)[selectedTemplateCategory]?.map((template: {name: string}) => (
            <option key={template.name} value={template.name}>
              {template.name}
            </option>
          ))}
        </select>
        <Button
          variant="primary" // Button componentinizin 'primary' variantı olduğundan emin olun
          size="sm"
          onClick={() => {
            if (selectedTemplate) {
              console.log('Preset uygulanıyor:', selectedTemplate);
              addSizeTemplate(selectedTemplate);
            } else {
              toast.warning('Lütfen önce bir preset seçin');
            }
          }}
          disabled={!selectedTemplate}
          className="w-full sm:w-auto"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Preseti Uygula
        </Button>
      </div>
    </div>
  );

  // a.tsx'den renderTable
  const renderTable = () => (
    <div className="bg-white p-4 rounded-md border border-gray-200">
      <div className="flex flex-col sm:flex-row items-center justify-between mb-4 gap-2">
        <h4 className="font-medium text-gray-800">Beden Ölçü Tablosu</h4>
        <div className="flex flex-wrap gap-2">
          <select
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            value={selectedSizeCategory}
            onChange={(e) => {
              console.log('Beden kategorisi değişti:', e.target.value);
              setSelectedSizeCategory(e.target.value as SizeCategory);
            }}
          >
            <option value="tops">Üst Giyim</option>
            <option value="bottoms">Alt Giyim</option>
            <option value="numeric">Numerik</option>
            <option value="kids">Çocuk</option>
            <option value="shoes">Ayakkabı</option>
          </select>
          <select
            id="sizeSelect" // Bu ID addSizeSpecFromPreset tarafından kullanılıyor
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">Beden Seçin</option>
            {standardSizes[selectedSizeCategory].map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => {
              console.log('Beden ekle butonuna tıklandı (preset listesinden)');
              addSizeSpecFromPreset();
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Beden Ekle (Listeden)
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              console.log('Satır ekle butonuna tıklandı');
              setModalType('row');
              setModalInput('');
              setModalVisible(true);
            }}
          >
            Ölçü Ekle (Satır)
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              console.log('Sütun ekle butonuna tıklandı');
              setModalType('column');
              setModalInput('');
              setModalVisible(true);
            }}
          >
            Beden Ekle (Sütun)
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto mt-4">
        <table className="min-w-full divide-y divide-gray-200 rounded-md shadow-sm overflow-hidden">
          <thead className="bg-indigo-100">
            <tr>
              <th className="px-4 py-2 w-40 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider sticky left-0 bg-indigo-100 z-10">
                Ölçü / Beden
              </th>
              {dynamicTableColumns.map((column, index) => (
                <th key={index} className="px-4 py-2 text-center text-xs font-semibold text-indigo-700 uppercase tracking-wider group relative">
                  <div className="flex items-center justify-center space-x-1">
                    {editingColName === column ? (
                      <>
                        <input
                          type="text"
                          value={editingColInput}
                          onChange={(e) => setEditingColInput(e.target.value)}
                          className="w-20 text-xs px-1 border rounded bg-white"
                          onBlur={handleColNameSave}
                          onKeyPress={(e) => e.key === 'Enter' && handleColNameSave()}
                          autoFocus
                        />
                        <button type="button" onClick={handleColNameSave} className="text-green-600 hover:text-green-800">✓</button>
                        <button type="button" onClick={handleColNameCancel} className="text-red-600 hover:text-red-800">×</button>
                      </>
                    ) : (
                      <span onClick={() => handleColNameEdit(column)} className="cursor-pointer">{column}</span>
                    )}
                    <button
                      type="button"
                      onClick={() => removeTableColumn(index)}
                      className="text-red-500 hover:text-red-700 opacity-50 group-hover:opacity-100 transition-opacity"
                      title="Sütunu Sil"
                    >
                      🗑️
                    </button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {dynamicTableRows.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-indigo-50">
                <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-700 sticky left-0 bg-white group relative hover:bg-indigo-50 z-0">
                  <div className="flex items-center justify-between">
                    {editingRowName === row ? (
                       <>
                        <input
                          type="text"
                          value={editingRowInput}
                          onChange={(e) => setEditingRowInput(e.target.value)}
                          className="w-28 text-sm px-1 border rounded"
                          onBlur={handleRowNameSave}
                          onKeyPress={(e) => e.key === 'Enter' && handleRowNameSave()}
                          autoFocus
                        />
                        <button type="button" onClick={handleRowNameSave} className="text-green-600 hover:text-green-800">✓</button>
                        <button type="button" onClick={handleRowNameCancel} className="text-red-600 hover:text-red-800">×</button>
                      </>
                    ) : (
                      <span onClick={() => handleRowNameEdit(row)} className="cursor-pointer flex-grow">{row}</span>
                    )}
                        <button
                          type="button"
                          onClick={() => removeTableRow(rowIndex)}
                        className="text-red-500 hover:text-red-700 opacity-50 group-hover:opacity-100 transition-opacity ml-2"
                        title="Satırı Sil"
                        >
                       🗑️
                        </button>
                  </div>
                </td>
                {dynamicTableColumns.map((column, colIndex) => (
                  <td key={colIndex} className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                    <input
                      type="text" // Genellikle sayılar için "number" daha iyi olabilir, ancak esneklik için "text"
                      value={dynamicTableData[row]?.[column] || ''}
                      onChange={(e) => updateTableCell(row, column, e.target.value)}
                      className="w-full h-8 px-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {dynamicTableRows.length === 0 && dynamicTableColumns.length === 0 && (
        <p className="text-center text-gray-500 mt-4">Tabloya veri eklemek için yukarıdaki butonları kullanın.</p>
      )}
    </div>
  );

  // a.tsx'den useEffect hook'ları
  useEffect(() => {
    // techSpec.dynamicSizeTable değiştiğinde veya component ilk yüklendiğinde tabloyu doldur
    if (techSpec.dynamicSizeTable && 
        (techSpec.dynamicSizeTable.rows !== dynamicTableRows || 
         techSpec.dynamicSizeTable.columns !== dynamicTableColumns || 
         JSON.stringify(techSpec.dynamicSizeTable.data) !== JSON.stringify(dynamicTableData))
    ) {
      console.log("techSpec.dynamicSizeTable'dan state güncelleniyor:", techSpec.dynamicSizeTable);
      setDynamicTableRows(techSpec.dynamicSizeTable.rows || []);
      setDynamicTableColumns(techSpec.dynamicSizeTable.columns || []);
      setDynamicTableData(techSpec.dynamicSizeTable.data || {});
    }
  }, [techSpec.dynamicSizeTable]);


  useEffect(() => {
    // Bu useEffect, techSpec._id değiştiğinde ürün verisini çekip tabloyu doldurmak için.
    // Mevcut TechnicalSpecSheet.tsx'de benzer bir yapı yoksa ve gerekmiyorsa kaldırılabilir.
    // Eğer ürün ID'sine göre veri çekme mantığı varsa ve tabloyu etkiliyorsa tutulmalı.
    const fetchProductData = async () => {
      if (!techSpec._id) return; // Eğer ID yoksa işlem yapma

      const requestInfo = {
        url: `${BACKEND_URL}/api/products/${techSpec._id}`, // API endpoint'inizi kontrol edin
        method: 'GET',
        timestamp: new Date().toISOString()
      };

      try {
        console.log('Ürün verisi alınıyor (ölçü tablosu için):', requestInfo.url);
        const response = await fetch(requestInfo.url);
        const productData = await response.json();

        if (!response.ok) {
          throw new Error(productData.error || 'Ürün verisi alınamadı');
        }
        
        // Gelen veride technicalSpec ve içinde dynamicSizeTable varsa tabloyu güncelle
        if (productData.technicalSpec && productData.technicalSpec.dynamicSizeTable) {
          const { rows, columns, data } = productData.technicalSpec.dynamicSizeTable;
          console.log("API'den gelen dynamicSizeTable:", { rows, columns, data });
          updateTableData(rows || [], columns || [], data || {});
        } else if (productData.technicalSpec && productData.technicalSpec.sizeSpecs && productData.technicalSpec.measurementFields) {
            // Eski yapıya göre fallback (sizeSpecs ve measurementFields varsa)
            console.log("API'den eski yapı geldi, dynamicSizeTable'a dönüştürülüyor.");
            const sizeSpecsFromAPI = productData.technicalSpec.sizeSpecs || [];
            const measurementFieldsFromAPI = productData.technicalSpec.measurementFields || [];

            const rows = measurementFieldsFromAPI;
            const columns = sizeSpecsFromAPI.map((spec: SizeSpec) => spec.size).filter((s: string) => s); // Sadece geçerli beden adlarını al
            
            const data: { [key: string]: { [key: string]: string } } = {};
          rows.forEach((row: string) => {
                data[row] = {};
            columns.forEach((col: string) => {
                    const spec = sizeSpecsFromAPI.find((s: SizeSpec) => s.size === col);
                    data[row][col] = spec && spec[row] !== undefined ? String(spec[row]) : '';
            });
          });
            updateTableData(rows, columns, data);

        } else {
          console.log("API'den gelen yanıtta dynamicSizeTable veya eski yapı bulunamadı.");
        }

      } catch (error: any) {
        console.error('Ürün verisi (ölçü tablosu) alınırken hata:', error);
        toast.error(`Ürün ölçü tablosu verisi alınamadı: ${error.message}`);
      }
    };
    
    // Eğer techSpec._id varsa ve tablo henüz doldurulmamışsa (veya belirli bir koşulda) fetchProductData çağrılabilir.
    // Örneğin, component mount olduğunda veya _id değiştiğinde.
    if (techSpec._id) {
    fetchProductData();
    }
  }, [techSpec._id]); // techSpec._id değiştiğinde çalışır


  // a.tsx'den saveToDatabase
  const saveToDatabase = async () => {
    if (!techSpec._id) {
      console.error('Ürün ID bulunamadı, kaydetme yapılamıyor.');
      toast.error('Kaydetmek için Ürün ID gereklidir.');
      return;
    }

    // Güncel tablo verilerini techSpec'e işle (zaten updateTableData içinde yapılıyor ama emin olmak için)
    const currentTechSpecForSave: TechnicalSpec = {
        ...techSpec,
        measurementFields: dynamicTableRows,
        // sizeSpecs, updateTableData tarafından zaten güncelleniyor.
          dynamicSizeTable: {
            rows: dynamicTableRows,
            columns: dynamicTableColumns,
            data: dynamicTableData
          }
    };
    
    console.log("Veritabanına kaydedilecek TechnicalSpec:", currentTechSpecForSave);


    try {
      // Sadece ölçü tablosu ile ilgili kısımları gönder
      const payload = {
        sizeSpecs: currentTechSpecForSave.sizeSpecs,
        measurementFields: currentTechSpecForSave.measurementFields,
        dynamicSizeTable: currentTechSpecForSave.dynamicSizeTable
      };

      const response = await fetch(`${BACKEND_URL}/api/products/${techSpec._id}/technical-spec`, { // API endpoint'inizi kontrol edin
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload) // Sadece ilgili kısımları gönder
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Veritabanına kaydetme başarısız oldu');
      }
      const responseData = await response.json();
      toast.success(responseData.message || 'Beden ölçü tablosu başarıyla kaydedildi');
      
      // İsteğe bağlı: Kayıttan sonra setTechSpec ile state'i sunucudan gelen veriyle senkronize edebilirsiniz
      // setTechSpec(responseData.product.technicalSpec); // Eğer sunucu güncellenmiş tam spec dönerse

    } catch (error: any) {
      console.error('Kaydetme hatası:', error);
      toast.error(`Beden ölçü tablosu kaydedilirken bir hata oluştu: ${error.message}`);
    }
  };


  // --- Diğer fonksiyonlar (Dikiş, Kumaş, Baskı vb.) ---
  // Bu fonksiyonlar TechnicalSpecSheet.tsx'den olduğu gibi kalır.
  // Sadece addSizeSpec, removeSizeSpec, updateSizeSpec, addCustomSizeField gibi eski tabloya ait olanlar çıkarıldı.

  const addStitchDetail = () => {
    setTechSpec({
      ...techSpec,
      stitchDetails: [
        ...(techSpec.stitchDetails || []),
        { location: '', stitchType: '', threadType: '', stitchesPerInch: 0, notes: '', imageUrls: [] }
      ]
    });
  };

  const updateStitchDetail = (index: number, field: string, value: string | number | string[]) => {
    const updatedStitchDetails = [...(techSpec.stitchDetails || [])];
    if(updatedStitchDetails[index]){
        (updatedStitchDetails[index] as any)[field] = field === 'stitchesPerInch' ? Number(value) : value;
        setTechSpec({ ...techSpec, stitchDetails: updatedStitchDetails });
    }
  };

  const removeStitchDetail = (index: number) => {
    const updatedStitchDetails = [...(techSpec.stitchDetails || [])];
    updatedStitchDetails.splice(index, 1);
    setTechSpec({ ...techSpec, stitchDetails: updatedStitchDetails });
  };
  
  const updateFabricSpec = (index: number, field: string, value: string) => {
    const updatedFabricSpecs = techSpec.fabricSpecs ? [...techSpec.fabricSpecs] : [];
    if (index === 0 && updatedFabricSpecs.length === 0) {
      updatedFabricSpecs.push({
        composition: '', weight: '', construction: '', supplier: '', colorFastness: '', shrinkage: '',
      });
    }
    if (updatedFabricSpecs[index]) {
      (updatedFabricSpecs[index]as any)[field] = value;
      setTechSpec({ ...techSpec, fabricSpecs: updatedFabricSpecs });
    } else {
      console.warn(`updateFabricSpec: Geçersiz index ${index}`);
    }
  };

  const addPrintingDetail = () => {
    setTechSpec({
      ...techSpec,
      printingDetails: [
        ...(techSpec.printingDetails || []),
        { technique: '', location: '', colors: '', size: '', notes: '', imageUrls: [] }
      ]
    });
  };

  const updatePrintingDetail = (index: number, field: string, value: string | string[]) => {
    const updatedPrintingDetails = [...(techSpec.printingDetails || [])];
     if(updatedPrintingDetails[index]){
        (updatedPrintingDetails[index] as any)[field] = value;
        setTechSpec({ ...techSpec, printingDetails: updatedPrintingDetails });
    }
  };
  
  const updatePrintingDetailImages = (index: number, newImageUrls: string[]) => {
    const updatedPrintingDetails = [...(techSpec.printingDetails || [])];
    if (updatedPrintingDetails[index]) {
        updatedPrintingDetails[index].imageUrls = newImageUrls;
        setTechSpec({ ...techSpec, printingDetails: updatedPrintingDetails });
    }
  };

  const removePrintingDetail = (index: number) => {
    const updatedPrintingDetails = [...(techSpec.printingDetails || [])];
    updatedPrintingDetails.splice(index, 1);
    setTechSpec({ ...techSpec, printingDetails: updatedPrintingDetails });
  };

  const handleImageUpload = async (index: number, files: FileList | null, detailType: 'print' | 'stitch') => {
    if (!files || files.length === 0) return;

    let currentImages: string[] = [];
    if (detailType === 'print' && techSpec.printingDetails && techSpec.printingDetails[index]) {
        currentImages = techSpec.printingDetails[index].imageUrls;
    } else if (detailType === 'stitch' && techSpec.stitchDetails && techSpec.stitchDetails[index]) {
        currentImages = techSpec.stitchDetails[index].imageUrls;
    } else {
        toast.error("Geçersiz detay index'i veya tipi.");
        return;
    }
    
    const availableSlots = 5 - currentImages.length;
    if (files.length > availableSlots) {
      toast.warn(`En fazla ${availableSlots} resim daha yükleyebilirsiniz.`);
      files = Array.from(files).slice(0, availableSlots) as unknown as FileList;
      if (files.length === 0) return;
    }

    const formData = new FormData();
    Array.from(files).forEach(file => formData.append('images', file));

    try {
      const response = await fetch(`${BACKEND_URL}/api/upload-images`, { method: 'POST', body: formData });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Resim yüklenemedi');
      }
      const result = await response.json();
      if (result.imageUrls && Array.isArray(result.imageUrls)) {
        const newImageUrls = [...currentImages, ...result.imageUrls];
        if (detailType === 'print') updatePrintingDetailImages(index, newImageUrls);
        // else if (detailType === 'stitch') updateStitchDetailImages(index, newImageUrls); // updateStitchDetailImages tanımlanmalı
         else if (detailType === 'stitch') { // updateStitchDetailImages yerine doğrudan updateStitchDetail kullanılabilir
            updateStitchDetail(index, 'imageUrls', newImageUrls);
        }
        toast.success(`${result.imageUrls.length} resim başarıyla yüklendi.`);
      } else {
        throw new Error('Sunucudan geçersiz yanıt formatı');
      }
    } catch (error: any) {
      console.error(`${detailType} resmi yükleme hatası:`, error);
      toast.error(`${detailType === 'print' ? 'Baskı' : 'Dikiş'} resmi yüklenirken hata: ${error.message}`);
    }
  };
  
  const handleImageRemove = (detailIndex: number, imageIndex: number, detailType: 'print' | 'stitch') => {
    let currentImagesList: string[][] | undefined;
    if (detailType === 'print') currentImagesList = techSpec.printingDetails?.map(p => p.imageUrls);
    else if (detailType === 'stitch') currentImagesList = techSpec.stitchDetails?.map(s => s.imageUrls);

    if (currentImagesList && currentImagesList[detailIndex]) {
        const updatedImages = [...currentImagesList[detailIndex]];
        updatedImages.splice(imageIndex, 1);
        if (detailType === 'print') updatePrintingDetailImages(detailIndex, updatedImages);
        // else if (detailType === 'stitch') updateStitchDetailImages(detailIndex, updatedImages);
        else if (detailType === 'stitch') updateStitchDetail(detailIndex, 'imageUrls', updatedImages);
        toast.info("Resim kaldırıldı.");
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    setDraggingIndex(index);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDraggingIndex(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, index: number, detailType: 'print' | 'stitch') => {
    e.preventDefault();
    setDraggingIndex(null);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleImageUpload(index, files, detailType);
    }
  };
  
  const addCareInstruction = (instruction: string) => {
    if (instruction && instruction.trim() !== "") {
      setTechSpec({
        ...techSpec,
        careInstructions: [...(techSpec.careInstructions || []), instruction.trim()]
      });
    }
  };

  const removeCareInstruction = (index: number) => {
    const updatedCareInstructions = [...(techSpec.careInstructions || [])];
    updatedCareInstructions.splice(index, 1);
    setTechSpec({ ...techSpec, careInstructions: updatedCareInstructions });
  };

  const addQualityStandard = (standard: string) => {
    if (standard && standard.trim() !== "") {
      setTechSpec({
        ...techSpec,
        qualityStandards: [...(techSpec.qualityStandards || []), standard.trim()]
      });
    }
  };
  
  const removeQualityStandard = (index: number) => {
    const updatedQualityStandards = [...(techSpec.qualityStandards || [])];
    updatedQualityStandards.splice(index, 1);
    setTechSpec({ ...techSpec, qualityStandards: updatedQualityStandards });
  };

  const updateNotes = (notes: string) => {
    setTechSpec({ ...techSpec, notes });
  };
  
  // Kumaş detayları için değerleri JSX dışında hesapla
  const fabricComposition = (techSpec.fabricSpecs && techSpec.fabricSpecs[0] && techSpec.fabricSpecs[0].composition) || '';
  const fabricWeight = (techSpec.fabricSpecs && techSpec.fabricSpecs[0] && techSpec.fabricSpecs[0].weight) || '';
  const fabricConstruction = (techSpec.fabricSpecs && techSpec.fabricSpecs[0] && techSpec.fabricSpecs[0].construction) || '';
  const fabricSupplier = (techSpec.fabricSpecs && techSpec.fabricSpecs[0] && techSpec.fabricSpecs[0].supplier) || '';
  const fabricColorFastness = (techSpec.fabricSpecs && techSpec.fabricSpecs[0] && techSpec.fabricSpecs[0].colorFastness) || '';
  const fabricShrinkage = (techSpec.fabricSpecs && techSpec.fabricSpecs[0] && techSpec.fabricSpecs[0].shrinkage) || '';


  return (
    <>
    <Card>
      <SectionTitle>Teknik Föy</SectionTitle>
      <div className="space-y-6 mt-4">
        {/* --- YENİ ÖLÇÜ TABLOSU BÖLÜMÜ --- */}
        {renderTemplateSelector()}
        {renderTable()}
        {/* --- YENİ ÖLÇÜ TABLOSU BÖLÜMÜ SONU --- */}

        <div className="bg-white p-4 rounded-md border border-gray-200">
          <h4 className="font-medium text-gray-800 mb-4">Kumaş Detayları</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Kumaş Kompozisyonu</label>
              <input type="text" placeholder="Örn: %95 Pamuk, %5 Elastan" value={fabricComposition} onChange={(e) => updateFabricSpec(0, 'composition', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"/>
          </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Kumaş Gramajı</label>
              <input type="text" placeholder="Örn: 180 g/m²" value={fabricWeight} onChange={(e) => updateFabricSpec(0, 'weight', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"/>
          </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Kumaş Yapısı</label>
              <input type="text" placeholder="Örn: 30/1 Single Jersey" value={fabricConstruction} onChange={(e) => updateFabricSpec(0, 'construction', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"/>
          </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Kumaş Tedarikçisi</label>
              <input type="text" placeholder="Tedarikçi firma adı" value={fabricSupplier} onChange={(e) => updateFabricSpec(0, 'supplier', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"/>
          </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Renk Haslığı</label>
              <input type="text" placeholder="Örn: 4/5 Yıkama" value={fabricColorFastness} onChange={(e) => updateFabricSpec(0, 'colorFastness', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"/>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Çekme Payı</label>
              <input type="text" placeholder="Örn: %3 En, %5 Boy" value={fabricShrinkage} onChange={(e) => updateFabricSpec(0, 'shrinkage', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"/>
            </div>
        </div>
      </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-md border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium text-gray-800">Dikiş Detayları</h4>
                    <Button variant="outline" size="sm" onClick={addStitchDetail}>
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                        Dikiş Ekle
                    </Button>
          </div>
                {Array.isArray(techSpec.stitchDetails) && techSpec.stitchDetails.length > 0 ? (
                    <div className="space-y-3">
                        {techSpec.stitchDetails.map((stitch, index) => (
                            <div key={index} className="border border-gray-200 rounded-md p-3 space-y-3">
                                <div className="flex justify-between mb-2">
                                    <span className="text-xs font-medium text-gray-500">Dikiş #{index + 1}</span>
                                    <button type="button" onClick={() => removeStitchDetail(index)} className="text-red-500 hover:text-red-700">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 00-1-1h-4a1 1 00-1 1v3M4 7h16" /></svg>
                                    </button>
          </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Bölge</label><input type="text" placeholder="Örn: Yan dikiş" value={stitch.location} onChange={(e) => updateStitchDetail(index, 'location', e.target.value)} className="w-full px-2 py-1 text-sm border rounded-md"/></div>
                                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Dikiş Tipi</label><input type="text" placeholder="Örn: Overlok" value={stitch.stitchType} onChange={(e) => updateStitchDetail(index, 'stitchType', e.target.value)} className="w-full px-2 py-1 text-sm border rounded-md"/></div>
                                    <div><label className="block text-xs font-medium text-gray-700 mb-1">İplik Tipi</label><input type="text" placeholder="Örn: 40/2 Polyester" value={stitch.threadType} onChange={(e) => updateStitchDetail(index, 'threadType', e.target.value)} className="w-full px-2 py-1 text-sm border rounded-md"/></div>
                                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Dikiş Sıklığı (SPI)</label><input type="number" placeholder="cm başına" value={stitch.stitchesPerInch} onChange={(e) => updateStitchDetail(index, 'stitchesPerInch', e.target.value)} className="w-full px-2 py-1 text-sm border rounded-md"/></div>
          </div>
                                <div><label className="block text-xs font-medium text-gray-700 mb-1">Notlar</label><input type="text" placeholder="Dikiş ile ilgili özel notlar" value={stitch.notes} onChange={(e) => updateStitchDetail(index, 'notes', e.target.value)} className="w-full px-2 py-1 text-sm border rounded-md"/></div>
                                
                                {/* Dikiş Resim Yükleme */}
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                    <label className="block text-xs font-medium text-gray-700 mb-2">Görseller (En fazla 5 adet)</label>
                                    <div className="flex flex-wrap gap-2 mb-2">
                                        {Array.isArray(stitch.imageUrls) && stitch.imageUrls.map((url, imgIndex) => (
                                            <div key={imgIndex} className="relative group"><img src={`${BACKEND_URL}${url}`} alt={`Dikiş ${index+1} Resim ${imgIndex+1}`} className="h-16 w-16 object-cover rounded border"/><button type="button" onClick={() => handleImageRemove(index, imgIndex, 'stitch')} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100"><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg></button></div>
                                        ))}
        </div>
                                    {(!stitch.imageUrls || stitch.imageUrls.length < 5) && (
                                        <div onDragOver={(e) => handleDragOver(e, index)} onDragLeave={handleDragLeave} onDrop={(e) => handleDrop(e, index, 'stitch')} className={`relative border-2 border-dashed rounded-md p-4 text-center cursor-pointer ${draggingIndex === index ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'}`} onClick={() => document.getElementById(`stitchFileInput-${index}`)?.click()}>
                                            <input type="file" id={`stitchFileInput-${index}`} multiple accept="image/*" className="hidden" onChange={(e) => handleImageUpload(index, e.target.files, 'stitch')}/>
                                            <svg className="w-8 h-8 mb-1 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                                            {draggingIndex === index ? 'Dosyaları buraya bırakın' : 'Sürükleyin veya tıklayıp seçin'}
                                            <span className="text-xs text-gray-400 mt-1 block">Kalan Hak: {5 - (stitch.imageUrls?.length || 0)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : <p className="text-center text-gray-500 py-4">Henüz dikiş detayı eklenmedi.</p>}
      </div>

            <div className="bg-white p-4 rounded-md border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium text-gray-800">Baskı/Nakış Detayları</h4>
                    <Button variant="outline" size="sm" onClick={addPrintingDetail}>
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                        Baskı/Nakış Ekle
                    </Button>
          </div>
                {Array.isArray(techSpec.printingDetails) && techSpec.printingDetails.length > 0 ? (
                    <div className="space-y-3">
                        {techSpec.printingDetails.map((print, index) => (
                            <div key={index} className="border border-gray-200 rounded-md p-3 space-y-3">
                                <div className="flex justify-between mb-2">
                                    <span className="text-xs font-medium text-gray-500">Baskı/Nakış #{index + 1}</span>
                                    <button type="button" onClick={() => removePrintingDetail(index)} className="text-red-500 hover:text-red-700">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 00-1-1h-4a1 1 00-1 1v3M4 7h16" /></svg>
                                    </button>
          </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Teknik</label>
                                        <select value={print.technique} onChange={(e) => updatePrintingDetail(index, 'technique', e.target.value)} className="w-full px-2 py-1 text-sm border rounded-md">
                                            <option value="">Seçiniz</option><option value="screen">Serigraf</option><option value="digital">Dijital</option><option value="transfer">Transfer</option><option value="embroidery">Nakış</option><option value="sublimation">Süblimasyon</option>
                                        </select>
        </div>
                                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Konum</label><input type="text" placeholder="Örn: Ön göğüs" value={print.location} onChange={(e) => updatePrintingDetail(index, 'location', e.target.value)} className="w-full px-2 py-1 text-sm border rounded-md"/></div>
                                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Renkler</label><input type="text" placeholder="Örn: Pantone 185C" value={print.colors} onChange={(e) => updatePrintingDetail(index, 'colors', e.target.value)} className="w-full px-2 py-1 text-sm border rounded-md"/></div>
                                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Boyut</label><input type="text" placeholder="Örn: 25x15 cm" value={print.size} onChange={(e) => updatePrintingDetail(index, 'size', e.target.value)} className="w-full px-2 py-1 text-sm border rounded-md"/></div>
      </div>
                                <div><label className="block text-xs font-medium text-gray-700 mb-1">Notlar</label><input type="text" placeholder="Baskı ile ilgili özel notlar" value={print.notes} onChange={(e) => updatePrintingDetail(index, 'notes', e.target.value)} className="w-full px-2 py-1 text-sm border rounded-md"/></div>

                                {/* Baskı Resim Yükleme */}
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                    <label className="block text-xs font-medium text-gray-700 mb-2">Görseller (En fazla 5 adet)</label>
                                    <div className="flex flex-wrap gap-2 mb-2">
                                        {Array.isArray(print.imageUrls) && print.imageUrls.map((url, imgIndex) => (
                                            <div key={imgIndex} className="relative group"><img src={`${BACKEND_URL}${url}`} alt={`Baskı ${index+1} Resim ${imgIndex+1}`} className="h-16 w-16 object-cover rounded border"/><button type="button" onClick={() => handleImageRemove(index, imgIndex, 'print')} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100"><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg></button></div>
                                        ))}
                                    </div>
                                    {(!print.imageUrls || print.imageUrls.length < 5) && (
                                        <div onDragOver={(e) => handleDragOver(e, index)} onDragLeave={handleDragLeave} onDrop={(e) => handleDrop(e, index, 'print')} className={`relative border-2 border-dashed rounded-md p-4 text-center cursor-pointer ${draggingIndex === index ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'}`} onClick={() => document.getElementById(`printFileInput-${index}`)?.click()}>
                                            <input type="file" id={`printFileInput-${index}`} multiple accept="image/*" className="hidden" onChange={(e) => handleImageUpload(index, e.target.files, 'print')}/>
                                            <svg className="w-8 h-8 mb-1 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                                            {draggingIndex === index ? 'Dosyaları buraya bırakın' : 'Sürükleyin veya tıklayıp seçin'}
                                            <span className="text-xs text-gray-400 mt-1 block">Kalan Hak: {5 - (print.imageUrls?.length || 0)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
        </div>
                ) : <p className="text-center text-gray-500 py-4">Henüz baskı/nakış detayı eklenmedi.</p>}
      </div>
    </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white p-4 rounded-md border border-gray-200">
            <h4 className="font-medium text-gray-800 mb-4">Bakım Talimatları</h4>
            <div className="flex flex-wrap gap-2 mb-3">
              {Array.isArray(techSpec.careInstructions) && techSpec.careInstructions.map((instruction, index) => (
                        <div key={index} className="flex items-center bg-indigo-50 text-indigo-700 rounded-full px-3 py-1 text-xs">
                            {instruction}
                            <button type="button" onClick={() => removeCareInstruction(index)} className="ml-1.5 text-indigo-400 hover:text-indigo-600"><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg></button>
                </div>
              ))}
            </div>
            <div className="flex items-center space-x-2">
                    <input id="careInstructionInput" type="text" placeholder="Yeni bakım talimatı ekle" className="flex-1 px-3 py-2 text-sm border rounded-md" onKeyPress={(e) => { if (e.key === 'Enter') { addCareInstruction(e.currentTarget.value); e.currentTarget.value = ''; }}}/>
                    <Button variant="outline" size="sm" onClick={() => { const input = document.getElementById('careInstructionInput') as HTMLInputElement; if (input) {addCareInstruction(input.value); input.value = '';}}}>Ekle</Button>
            </div>
          </div>
          <div className="bg-white p-4 rounded-md border border-gray-200">
            <h4 className="font-medium text-gray-800 mb-4">Kalite Standartları</h4>
            <div className="flex flex-wrap gap-2 mb-3">
              {Array.isArray(techSpec.qualityStandards) && techSpec.qualityStandards.map((standard, index) => (
                        <div key={index} className="flex items-center bg-green-50 text-green-700 rounded-full px-3 py-1 text-xs">
                            {standard}
                            <button type="button" onClick={() => removeQualityStandard(index)} className="ml-1.5 text-green-400 hover:text-green-600"><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg></button>
                </div>
              ))}
            </div>
            <div className="flex items-center space-x-2">
                    <input id="qualityStandardInput" type="text" placeholder="Yeni kalite standardı ekle" className="flex-1 px-3 py-2 text-sm border rounded-md" onKeyPress={(e) => { if (e.key === 'Enter') { addQualityStandard(e.currentTarget.value); e.currentTarget.value = ''; }}}/>
                    <Button variant="outline" size="sm" onClick={() => { const input = document.getElementById('qualityStandardInput') as HTMLInputElement; if (input) {addQualityStandard(input.value); input.value = '';}}}>Ekle</Button>
                </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-md border border-gray-200">
            <h4 className="font-medium text-gray-800 mb-4">Ek Notlar</h4>
            <textarea value={techSpec.notes || ''} onChange={(e) => updateNotes(e.target.value)} rows={4} className="w-full px-3 py-2 text-sm border rounded-md" placeholder="Ürün ile ilgili ek notlar..."/>
          </div>
        </div>
      </Card>





    {/* Modal (a.tsx'den alındı) */}
      {modalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-96">
            <h3 className="text-lg font-medium mb-4">
            {modalType === 'row' ? 'Yeni Ölçü Ekle (Satır)' : 'Yeni Beden Ekle (Sütun)'}
            </h3>
            <input
              type="text"
              value={modalInput}
              onChange={(e) => setModalInput(e.target.value)}
            placeholder={modalType === 'row' ? 'Ölçü adı (örn: Göğüs)' : 'Beden adı (örn: XXL)'}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
              onKeyPress={(e) => e.key === 'Enter' && handleModalSubmit()}
            autoFocus
            />
            <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setModalVisible(false)}>İptal</Button>
            <Button variant="primary" size="sm" onClick={handleModalSubmit}>Ekle</Button>
            </div>
          </div>
        </div>
      )}


    </>
  );
};

export default TechnicalSpecSheet;
